import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { theme } from "../../theme/colors";
import { authenticatedAxios } from "../../utils/apiClient";

export default function RecoveryEmailSection({ currentRecoveryEmail }) {
  const [step, setStep] = useState("idle"); // idle | entering | codeSent | verified
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [savedEmail, setSavedEmail] = useState(currentRecoveryEmail || "");

  useEffect(() => {
    if (currentRecoveryEmail) setSavedEmail(currentRecoveryEmail);
  }, [currentRecoveryEmail]);

  const handleRequestCode = async () => {
    setError("");
    setSending(true);
    try {
      await authenticatedAxios("post", "/api/users/me/recovery-email/request-code/", { email });
      setStep("codeSent");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to send code. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleVerifyCode = async () => {
    setError("");
    setVerifying(true);
    try {
      const res = await authenticatedAxios("post", "/api/users/me/recovery-email/verify-code/", { email, code });
      setSavedEmail(res.data.recovery_email);
      setStep("verified");
      setEmail("");
      setCode("");
      setTimeout(() => setStep("idle"), 3000);
    } catch (err) {
      setError(err.response?.data?.error || "Invalid or expired code.");
    } finally {
      setVerifying(false);
    }
  };

  const handleStartChange = () => {
    setStep("entering");
    setEmail("");
    setCode("");
    setError("");
  };

  const handleCancel = () => {
    setStep("idle");
    setEmail("");
    setCode("");
    setError("");
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, color: "#111", mb: 2 }}>
        Recovery Email
      </Typography>

      <Box sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0" }}>
        <Typography variant="body2" sx={{ color: "#334155", mb: 2, fontSize: "0.85rem" }}>
          Used by CivixThread support to verify your identity if you lose access to your 2FA app or primary email.
        </Typography>

        {savedEmail && step === "idle" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <CheckCircleOutlineIcon sx={{ color: theme.primary, fontSize: 20 }} />
            <Typography sx={{ color: "#0f172a", fontWeight: 600, fontSize: "0.9rem" }}>
              {savedEmail}
            </Typography>
          </Box>
        )}

        {step === "idle" && (
          <Button
            variant="contained"
            onClick={handleStartChange}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              bgcolor: theme.primary,
              color: "#ffffff",
              "&:hover": { bgcolor: theme.primaryHover },
              "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" },
            }}
          >
            {savedEmail ? "Change Recovery Email" : "Add Recovery Email"}
          </Button>
        )}

        {step === "entering" && (
          <Box>
            <TextField
              fullWidth
              size="small"
              type="email"
              placeholder="your-backup@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleRequestCode}
                disabled={!email.trim() || sending}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  bgcolor: theme.primary,
                  color: "#ffffff",
                  "&:hover": { bgcolor: theme.primaryHover },
                  "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" },
                }}
              >
                {sending ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Send Verification Code"}
              </Button>
              <Button
                variant="outlined"
                onClick={handleCancel}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#334155",
                  borderColor: "#e2e8f0",
                  "&:hover": { bgcolor: "#f1f5f9", borderColor: "#cbd5e1" },
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        )}

        {step === "codeSent" && (
          <Box>
            <Typography variant="body2" sx={{ color: "#0f172a", mb: 1.5, fontWeight: 500 }}>
              We sent a 6-digit code to <strong>{email}</strong>. Enter it below:
            </Typography>
            <TextField
              size="small"
              placeholder="000000"
              value={code}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                setCode(val);
              }}
              inputProps={{
                maxLength: 6,
                style: { fontSize: 24, letterSpacing: 8, textAlign: "center", fontWeight: 700 },
                inputMode: "numeric",
              }}
              sx={{ mb: 2, width: 220, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleVerifyCode}
                disabled={code.length !== 6 || verifying}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  bgcolor: theme.primary,
                  color: "#ffffff",
                  "&:hover": { bgcolor: theme.primaryHover },
                  "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" },
                }}
              >
                {verifying ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Verify & Save"}
              </Button>
              <Button
                variant="outlined"
                onClick={handleCancel}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#334155",
                  borderColor: "#e2e8f0",
                  "&:hover": { bgcolor: "#f1f5f9", borderColor: "#cbd5e1" },
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        )}

        {step === "verified" && (
          <Alert severity="success" sx={{ fontWeight: 600 }}>
            Recovery email verified and saved!
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2, color: "#dc2626" }}>
            {error}
          </Alert>
        )}
      </Box>
    </Box>
  );
}
